/**
 * Small primitives shared across the Supervisor Panel:
 *   • ``Modal`` — dark-themed centred overlay with header + body
 *   • ``Field`` — label + hint wrapper for form inputs
 *   • ``IconBtn`` — 32×32 icon button with 4 colour variants
 *   • ``inputCls`` — the standard input className string
 *
 * Extracted from the monolithic ``SupervisorDashboard.jsx`` (Feb 2026
 * refactor batch 1) so dialogs and tabs can be moved out to their own
 * files without duplicating the styling.
 */

export const inputCls = "w-full h-10 px-3 rounded-lg bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm outline-none focus:border-amber-500";

export function Modal({ children, onClose, title }) {
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm grid place-items-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="font-bold text-lg text-zinc-100">{title}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200">✕</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <label className="block mb-3">
      <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-zinc-600 mt-1">{hint}</div>}
    </label>
  );
}

export function IconBtn({ children, onClick, title, color = "zinc", testid }) {
  const colors = {
    zinc: "hover:bg-zinc-800 text-zinc-400",
    rose: "hover:bg-rose-900/40 text-rose-400",
    amber: "hover:bg-amber-900/40 text-amber-400",
    emerald: "hover:bg-emerald-900/40 text-emerald-400",
    blue: "hover:bg-blue-900/40 text-blue-400",
  };
  return (
    <button
      title={title}
      onClick={onClick}
      data-testid={testid}
      className={`w-8 h-8 grid place-items-center rounded ${colors[color]}`}
    >
      {children}
    </button>
  );
}
